import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { MEETING_KINDS, emptyAgendaItem, emptyMeetingForm, governanceError, mergeParticipants, meetingPayload, meetingProblem, newKey, withOrganizer } from "../lib/governance";
import { getActiveZone, zoneLabel } from "../lib/time";
import { InterestedField, LinkList, LinkPicker, useGovernancePeople } from "../components/governance/GovernanceBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const NO_ORGANIZER = "__none__";

function Section({ title, hint, children }) {
  return (
    <Card className="p-4 sm:p-5 space-y-3">
      <div><h2 className="text-base font-semibold text-slate-900">{title}</h2>{hint && <p className="text-sm text-slate-500">{hint}</p>}</div>
      {children}
    </Card>
  );
}

// Planning a meeting or changing what is planned. The minutes are written on the meeting's own page, once it takes place.
export default function GovernanceMeetingForm() {
  const { meetingId } = useParams();
  const editing = !!meetingId;
  const navigate = useNavigate();
  const people = useGovernancePeople();
  const [form, setForm] = useState(() => emptyMeetingForm());
  const [source, setSource] = useState(null);
  const [phase, setPhase] = useState(editing ? "loading" : "ready");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key, setKey] = useState(newKey);
  const locked = !!source && !["planned", "held"].includes(source.status);

  useEffect(() => {
    if (!meetingId) return undefined;
    let alive = true;
    api.get(`/governance/meetings/${meetingId}`).then(({ data }) => { if (alive) { setSource(data); setForm(emptyMeetingForm(data)); setPhase("ready"); } })
      .catch(() => { if (alive) setPhase("error"); });
    return () => { alive = false; };
  }, [meetingId]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setKey(newKey()); };
  const problem = useMemo(() => meetingProblem(form), [form]);
  const organizer = form.participants.find((p) => p.role === "organizer" && p.type === "account")?.id || "";
  const setAgenda = (i, patch) => set({ agenda: form.agenda.map((a, j) => (j === i ? { ...a, ...patch } : a)) });

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = meetingPayload(form);
      let data;
      if (editing) {
        ({ data } = await api.patch(`/governance/meetings/${meetingId}`, { ...(locked ? { links: body.links } : body), expected_version: source.version }));
        toast.success("Réunion modifiée");
      } else {
        ({ data } = await api.post("/governance/meetings", body, { headers: { "Idempotency-Key": key } }));
        toast.success("Réunion planifiée");
      }
      navigate(`/gouvernance/reunions/${data.id}`);
    } catch (e) {
      setError(governanceError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-48 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-2" data-testid="meeting-form-error">
        <p className="text-sm text-slate-700">Cette réunion est introuvable ou vous n'y avez pas accès.</p>
        <Button asChild variant="outline"><Link to="/gouvernance/reunions">Retour aux réunions</Link></Button>
      </Card>
    );
  }
  const back = editing ? `/gouvernance/reunions/${meetingId}` : "/gouvernance/reunions";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="meeting-form">
      <div>
        <Link to={back} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Retour</Link>
        <h1 className="font-display font-bold text-2xl text-slate-900 mt-1">{editing ? `Modifier ${source.number}` : "Nouvelle réunion"}</h1>
        <p className="text-sm text-slate-500">Indiquez l'objet, le moment, les participants et l'ordre du jour. Le compte rendu s'écrit pendant ou après la réunion.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <div className="space-y-4 min-w-0">
          {locked && <p className="rounded-md bg-amber-50 text-amber-900 text-sm px-3 py-2" data-testid="meeting-locked-note">Cette réunion est close ou annulée : seuls les liens peuvent encore être ajustés.</p>}
          <Section title="L'essentiel">
            <div className="space-y-1.5"><Label htmlFor="mt-title">Objet</Label>
              <Input id="mt-title" value={form.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} className="h-10" disabled={locked} data-testid="meeting-title" /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={form.kind || undefined} onValueChange={(kind) => set({ kind })} disabled={locked}>
                  <SelectTrigger className="h-10" aria-label="Type de réunion" data-testid="meeting-kind"><SelectValue placeholder="Choisir un type" /></SelectTrigger>
                  <SelectContent>{MEETING_KINDS.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label htmlFor="mt-place">Lieu</Label>
                <Input id="mt-place" value={form.place} maxLength={200} onChange={(e) => set({ place: e.target.value })} className="h-10" disabled={locked} data-testid="meeting-place" placeholder="Bureau, salle…" /></div>
              <div className="space-y-1.5"><Label htmlFor="mt-start">Début</Label>
                <Input id="mt-start" type="datetime-local" value={form.starts_at} onChange={(e) => set({ starts_at: e.target.value })} className="h-10" disabled={locked} data-testid="meeting-start" /></div>
              <div className="space-y-1.5"><Label htmlFor="mt-end">Fin (facultatif)</Label>
                <Input id="mt-end" type="datetime-local" value={form.ends_at} onChange={(e) => set({ ends_at: e.target.value })} className="h-10" disabled={locked} data-testid="meeting-end" /></div>
            </div>
            <p className="text-xs text-slate-500 -mt-1" data-testid="meeting-zone-hint">Heures saisies en <strong className="font-medium text-slate-700">heure de {zoneLabel(getActiveZone())}</strong> : chaque participant les lit dans son propre fuseau. Se règle dans votre profil.</p>
            <div className="space-y-1.5"><Label htmlFor="mt-video">Lien de visioconférence (facultatif)</Label>
              <Input id="mt-video" value={form.video_url} maxLength={500} onChange={(e) => set({ video_url: e.target.value })} className="h-10" disabled={locked} data-testid="meeting-video" placeholder="https://…" />
              <p className="text-xs text-slate-500">Le CRM ne s'y connecte pas : il garde seulement le lien.</p></div>
          </Section>
          <Section title="Participants" hint="La présence se note pendant la réunion. Une personne hors du CRM n'est connue que par son nom.">
            <div className="space-y-1.5"><Label>Organisateur</Label>
              <Select value={organizer || NO_ORGANIZER} onValueChange={(v) => set({ participants: withOrganizer(form.participants, v === NO_ORGANIZER ? "" : v, people) })} disabled={locked}>
                <SelectTrigger className="h-10" aria-label="Organisateur" data-testid="meeting-organizer"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NO_ORGANIZER}>Personne en particulier</SelectItem>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Autres participants</Label>
              <InterestedField value={form.participants} people={people} onChange={(picked) => set({ participants: mergeParticipants(form.participants, picked) })} /></div>
          </Section>
          <Section title="Ordre du jour" hint="Un point par sujet à traiter. Les décisions pourront s'y rattacher.">
            <div className="space-y-2">
              {form.agenda.map((a, i) => (
                <div key={a.id || i} className="rounded-md border border-slate-200 p-3 space-y-2" data-testid={`agenda-${i}`}>
                  <div className="flex gap-2">
                    <Input value={a.title} maxLength={200} onChange={(e) => setAgenda(i, { title: e.target.value })} placeholder={`Point ${i + 1}`} className="h-10" disabled={locked} aria-label={`Point ${i + 1}`} data-testid={`agenda-title-${i}`} />
                    {!locked && <Button type="button" variant="ghost" className="h-10 w-10 p-0 shrink-0" onClick={() => set({ agenda: form.agenda.filter((_, j) => j !== i) })} aria-label={`Retirer le point ${i + 1}`}><Trash2 size={15} /></Button>}
                  </div>
                  <Textarea rows={2} maxLength={1000} value={a.note} onChange={(e) => setAgenda(i, { note: e.target.value })} placeholder="Note (facultatif)" disabled={locked} aria-label={`Note du point ${i + 1}`} />
                </div>
              ))}
              {!locked && form.agenda.length < 30 && <Button type="button" variant="outline" size="sm" onClick={() => set({ agenda: [...form.agenda, emptyAgendaItem()] })} data-testid="agenda-add"><Plus size={14} className="mr-1.5" />Ajouter un point</Button>}
            </div>
          </Section>
          <Section title="Liens" hint="Rattachez la réunion à ce qu'elle concerne. Un lien ne modifie rien dans l'autre module.">
            <LinkList links={form.links} openable={false} onRemove={(l) => set({ links: form.links.filter((x) => x !== l) })} />
            <LinkPicker value={form.links} onChange={(links) => set({ links })} />
          </Section>
        </div>
        <div className="space-y-4 lg:sticky lg:top-4">
          <Card className="p-4">
            <label className="flex items-start justify-between gap-3 cursor-pointer">
              <span><span className="block text-sm font-medium text-slate-900">Réunion restreinte</span><span className="block text-xs text-slate-500">Visible seulement de son créateur, de ses participants du CRM et des personnes qui voient tout.</span></span>
              <Switch checked={form.restricted} onCheckedChange={(restricted) => set({ restricted })} disabled={locked} aria-label="Réunion restreinte" data-testid="meeting-restricted-switch" />
            </label>
          </Card>
          <Card className="p-4 space-y-2 text-sm text-slate-600">
            <h3 className="font-semibold text-slate-900">Ce qui se passe ensuite</h3>
            <p>Le jour J, « Ouvrir la réunion » : notez la présence et écrivez le compte rendu. « Clore » le fige ; une correction devient un addendum daté.</p>
            <p>Les décisions et les actions (des tâches ordinaires) se créent depuis la page de la réunion.</p>
          </Card>
        </div>
      </div>
      {error && <p className="text-sm text-red-600" role="alert" data-testid="meeting-form-submit-error">{error}</p>}
      <div className="flex flex-wrap gap-2 justify-end">
        <Button asChild variant="outline" type="button"><Link to={back}>Annuler</Link></Button>
        <Button type="submit" disabled={busy || (!locked && !!problem)} title={(!locked && problem) || undefined} data-testid="meeting-submit">
          {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{editing ? "Enregistrer" : "Planifier la réunion"}
        </Button>
      </div>
    </form>
  );
}
